import React, { useEffect, useMemo, useState } from 'react';
import '../Card.css';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Divider, Menu, MenuItem, Tooltip, styled } from '@mui/material';
import { updateFormatForDm } from '../../../redux/DynamicMeasures/DynamicMeasuresActions';
import { CardOption } from '../CardOption';
import {
  checkIfMissingColumn,
  findNewDisplayName,
  getRoundingDigits,
} from '../../CommonFunctions/CommonFunctions';
import { TooltipProps } from '@mui/material/Tooltip';
import { setDisplayName } from '../setDisplayName';
import { messages } from '../../CommonFunctions/aliases';
import { useSelector, useDispatch } from 'react-redux';
import SnackBar from '../../SnackBar';
import WindowFunctionButton from './WindowFunctionButton';
import OverrideFunction from './OverrideFunction';
import RollUpButton from './RollUpButton';
import AggregationDisplay from './AggregationDisplay';
import AggregationMenuItem from './AggregationMenuItem';
import TimeGrainMenuItem from './TimeGrainMenuItem';
import WindowFunctionMenuItem from './WindowFunctionMenuItem';
import OverrideMenuItem from './OverrideMenuItem';
import MenuOptions from './MenuOptions';
import { RootState } from '../../../redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import {
  callQueryAPI,
  updateFormatOption,
  updateExtraMeasureField,
  enableOverrideForExtraMeasure,
  SetModifyOptions,
  removeMeasureMappingColumn,
} from '../../../redux/ChartPoperties/ChartControlsActions';
import RenameFunction from '../CardComponents/RenameFunction';
import WindowFunction from '../CardComponents/WindowFunction/WindowFunction';
import { createChartAxesForUID } from '../../../redux/ChartPoperties/ChartPropertiesActions';
import Warning from '@mui/icons-material/WarningAmber';
import SourceDetails from './SourceDetails';
import IgnoreReportFilterMenuItem from './IgnoreReportFilterOption';
import WarningIcon from '@mui/icons-material/WarningAmber';

const CustomTooltip = styled(
  ({ className, ...props }: TooltipProps & { isShowWarning?: boolean }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
)(({ isShowWarning }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: 'white',
    color: isShowWarning ? '#850000ff' : '#2bb9bb',
    fontSize: '13px',
    border: `1px solid ${isShowWarning ? '#850000ff' : '#2bb9bb'}`,
  },
  [`& .MuiTooltip-arrow`]: {
    color: 'white',
    '&::before': {
      width: '10px', // Adjust arrow width
      height: '14px', // Adjust arrow height
      border: `1px solid ${isShowWarning ? '#850000ff' : '#2bb9bb'}`,
    },
  },
}));

interface Props {
  field: {
    uId?: string;
    fieldname?: string;
    displayname?: string;
    displayName2?: string;
    dataType: string;
    prefix?: any;
    tableId?: string;
    agg?: any;
    aggname?: string;
    timeGrain?: any;
    disableReportFilterForOverride?: boolean;
    rollupDepth?: boolean;
    override?: any;
    isCalculatedField?: boolean;
    isAggregated?: boolean;
    windowfn?: any;
    SavedCalculationUUID?: string;
    hasOverridedFlow?: boolean;
  };
  bIndex: number;
  itemIndex: number;
  propKey: string;
  axisTitle: string;
  uID: string;
  formatObject: any;
  filterAxis: any;
  dimensionAxis: any;
  binName: string;
  extraMeasureName: string;
}

/**
 * @returns each individual table field dropped in extra measure section.
 * Each card has some aggregate values and option to select different aggregate and/or timeGrain values
 */

//Most of the commented code is related to window function and override will be added later. Some code is for measure axis that is copied from Card.tsx but must be changed
const ExtraMeasureCard = ({
  field,
  bIndex,
  itemIndex,
  propKey,
  axisTitle,
  uID,
  binName,
}: Props) => {
  const {
    tabTileProps,
    chartProp,
    dynamicMeasureState,
    tooltipFormats,
    chartData,
    savedCalcs,
    currentOperator,
    savedMeasureMappings,
  } = useSelector((state: RootState) => {
    const chartControl = state.chartControls.properties[propKey];
    const currentOperator = chartControl?.columnSecurity?.currentOperator?.toLowerCase();
    const savedMeasureMappings =
      chartControl?.columnSecurity?.filterPanels?.[currentOperator]?.filters?.[1]?.mapping ?? [];
    return {
      tabTileProps: state.tabTileProps,
      chartProp: state.chartProperties,
      dynamicMeasureState: state.dynamicMeasuresState,
      tooltipFormats: state.chartControls.properties[propKey]?.formatOptions?.tooltipFormats,
      chartData: state.chartControls.properties[propKey]?.chartData,
      savedCalcs: state.calculations.savedCalculations,
      currentOperator,
      savedMeasureMappings,
    };
  });
  const [showSourceDetails, setShowSourceDetails] = useState<boolean>(false);

  const extraMeasureField = tooltipFormats?.extraMeasureField;
  const allMeasureFields = JSON.parse(JSON.stringify(extraMeasureField[bIndex]?.fields));

  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  field.dataType = field?.dataType?.toLowerCase();

  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [disable, setDisable] = useState<{
    windowFn: boolean;
    override: boolean;
  }>({
    windowFn: false,
    override: false,
  });
  let chartType =
    chartProp.properties[`${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`].chartType;

  const measureMappingWarning =
    savedMeasureMappings.length > 0 &&
    !savedMeasureMappings.find((mapping: any) => mapping[field.uId || '']);

  //----------In future we have to need to work for richText ------------//

  // useEffect to manage override state
  useEffect(() => {
    if (tooltipFormats?.enableOverrideForExtraMeasureUID === '') {
      setOverrideFn(false);
    }
  }, [tooltipFormats?.enableOverrideForExtraMeasureUID]);

  useEffect(() => {
    // enable window function when there is chart data
    if (chartData?.length > 0) {
      setWindowFunctionDisable(false);
    } else {
      setWindowFunctionDisable(true);
      // Remove window function from ALL extra measure fields when chart data disappears
      if (extraMeasureField && extraMeasureField[bIndex]?.fields) {
        const updatedFields = extraMeasureField[bIndex].fields.map((field: any) => {
          if (field.windowfn) {
            // Clear window function and regenerate display name
            let field2 = { ...field, windowfn: null };
            field2 = setDisplayName(field2, binName, chartProp.properties[propKey].chartType);
            field2.displayname = findNewDisplayName(
              chartProp.properties[propKey].chartAxes,
              field2,
              tooltipFormats,
            );
            field2.isTextRenamed = false;
            return field2;
          }
          return field;
        });

        // Check if any field had window function that needs to be cleared
        const hasWindowFn = extraMeasureField[bIndex].fields.some((field: any) => field.windowfn);

        if (hasWindowFn) {
          const updatedExtraMeasureField = [...extraMeasureField];
          updatedExtraMeasureField[bIndex] = {
            ...updatedExtraMeasureField[bIndex],
            fields: updatedFields,
          };

          handleUpdateFormat('extraMeasureField', updatedExtraMeasureField, 'tooltipFormats');
        }
      }
    }
  }, [chartData]);

  useEffect(() => {
    //If two dimensional charts dimension, row, column, distribution without any fields, then window function will get disable
    let disableWindowFn = false;
    let _disableOverride = false;

    if (['heatmap', 'crossTab', 'boxPlot', 'bubbleMap'].includes(chartType)) {
      if (
        chartProp.properties[propKey].chartAxes[1].fields.length === 0 &&
        chartProp.properties[propKey].chartAxes[2].fields.length === 0
      ) {
        disableWindowFn = true;
        setDisable({
          ...disable,
          windowFn: true,
        });
        //while window function get disabled, it check window function having any values in redux state. If yes, then window function value will be null
        if (
          extraMeasureField[bIndex]?.fields &&
          extraMeasureField[bIndex].fields[itemIndex]?.windowfn
        ) {
          let field2 = JSON.parse(JSON.stringify(field));
          field2.windowfn = null;
          dispatch(updateExtraMeasureField(propKey, bIndex, itemIndex, field2));
        }
      } else {
        //If two dimensional charts dimension, row, column, distribution with fields, then window function will get enable
        if (
          chartProp.properties[propKey].chartAxes[1].fields.length > 0 ||
          chartProp.properties[propKey].chartAxes[2].fields.length > 0
        ) {
          setDisable({
            ...disable,
            windowFn: false,
          });
          disableWindowFn = false;
        }
      }
    } else {
      //If one dimensional charts dimension or row without any fields, then window function will get disable
      if (!['heatmap', 'crossTab', 'boxPlot', 'richText', 'bubbleMap'].includes(chartType)) {
        if (chartProp.properties[propKey].chartAxes[1].fields.length === 0) {
          setDisable({
            ...disable,
            windowFn: true,
          });
          disableWindowFn = true;
          //while window function get disabled, it check window function having any values in redux state. If yes, then window function value will be null
          if (
            extraMeasureField[bIndex]?.fields &&
            extraMeasureField[bIndex].fields[itemIndex]?.windowfn
          ) {
            let field2 = JSON.parse(JSON.stringify(field));
            field2.windowfn = null;
            dispatch(updateExtraMeasureField(propKey, bIndex, itemIndex, field2));
          } else {
            //If one dimensional charts dimension or row with fields, then window function will get enable
            if (chartProp.properties[propKey].chartAxes[1].fields.length > 0) {
              setDisable({
                ...disable,
                windowFn: false,
              });
              disableWindowFn = false;
            }
          }
        }
      }
    }
    const calculation = field?.SavedCalculationUUID
      ? savedCalcs.find((calc) => calc.uuid === field?.SavedCalculationUUID)
      : null;
    if (calculation) {
      let disableWin = false;
      let disabledOverride = false;
      const info = calculation.calculationInfo;
      Object.values(info.flows).some((steps) =>
        steps.some((step) => {
          if (step.flowOverride?.windowFunction) {
            disableWin = true;
          }
          disabledOverride =
            !!step.flowOverride &&
            (step.flowOverride.dimension?.type !== 'honorAll' ||
              step.flowOverride.filter?.type !== 'honorAll');
          if (disableWin && disabledOverride) return true;
          return false;
        }),
      );
      disableWindowFn = disableWin || disableWindowFn;
      _disableOverride = disabledOverride;
      /**
       * New requirement on 20-04-2026
       * If formula measure has  overrided flow or window function
       * then in chart axis(measure) we wont be able to add override and window function both
       */
      setDisable({
        windowFn: disableWindowFn || disabledOverride,
        override: disableWin || disabledOverride,
      });
      return;
    }
    setDisable({
      windowFn: disableWindowFn,
      override: _disableOverride,
    });
  }, [chartProp.properties[propKey].chartAxes]);

  const deleteItem = async () => {
    // let showSnackBar = false;
    let tempExtraMeasureFieldsList = JSON.parse(JSON.stringify(allMeasureFields));
    tempExtraMeasureFieldsList.splice(itemIndex, 1);
    const isLastField = tempExtraMeasureFieldsList?.length === 0 ? true : false;
    handleUpdateFormat('isLastField', isLastField, 'tooltipFormats');
    //-------------Create a copy of the extraMeasureField array and update extra Measure field after removing a field-----------//
    const updatedExtraMeasureField = [...extraMeasureField];
    updatedExtraMeasureField[bIndex] = {
      ...updatedExtraMeasureField[bIndex],
      fields: tempExtraMeasureFieldsList,
    };
    handleUpdateFormat('extraMeasureField', updatedExtraMeasureField, 'tooltipFormats');
    if (tooltipFormats?.extraMeasureField[bIndex].fields[itemIndex]?.override) {
      // ------------This dispatch is for measure but we need to change when we use extra measure override functionality --------------//
      // dispatch(enableOverrideForUIDAction(propKey, ''));
    }

    // To close the override panel if it is open
    if (
      tooltipFormats?.extraMeasureField[bIndex].fields[itemIndex].uId ===
      tooltipFormats?.enableOverrideForExtraMeasureUID
    ) {
      dispatch(enableOverrideForExtraMeasure(propKey, ''));
    }

    //---delete measure formats for that extra measure in tooltip formats---//
    const deletedMeasureUid = field.uId;
    const measureFormats = tooltipFormats?.measureFormats;
    if (deletedMeasureUid && measureFormats && measureFormats[deletedMeasureUid]) {
      const { [deletedMeasureUid]: _, ...remainingFormats } = measureFormats;
      handleUpdateFormat('measureFormats', remainingFormats, 'tooltipFormats');
    }
    if (deletedMeasureUid) {
      dispatch(removeMeasureMappingColumn(propKey, currentOperator, deletedMeasureUid));
    }
  };

  let currentChartAxesName = axisTitle;

  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [anchorElment, setAnchorElement] = useState<any | null>(null);

  //Window function open/close and enable/disable
  const [windowFunction, setWindowFunction] = useState<boolean>(false);
  const [overrideFn, setOverrideFn] = useState<boolean>(false);
  const [windowFunctionDisable, setWindowFunctionDisable] = useState<boolean>(false);
  const [renameFunction, setRenameFunction] = useState<boolean>(false);
  // const [renameText, setRenameText] = useState<string>("");

  const [showTooltip, setShowTooltip] = useState(false);

  const selectedDs = useMemo(() => {
    return chartProp.properties[propKey].selectedDs;
  }, [chartProp.properties[propKey].selectedDs, chartProp.properties[propKey].oldSelectedDs]);
  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

  const open: boolean = Boolean(anchorEl);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);

    setShowTooltip(false);
  };

  /**
   * Handles updating format options in Redux state
   * @param option - The specific option to update (e.g., 'measureFormats', 'displayname', 'agg')
   * @param value - The new value for the option
   * @param optionKey - The format type key (e.g., 'tooltipFormats', 'labelFormats')
   */
  const handleUpdateFormat = (option: string, value: any, optionKey?: string): void => {
    try {
      if (chartType === 'richText') {
        // For richText charts, use dynamic measure actions
        const dmKey = `${dynamicMeasureState.selectedTileId}.${dynamicMeasureState.selectedDynamicMeasureId}`;
        dispatch(updateFormatForDm(dmKey, option, value));
      } else {
        // For regular charts, use chart controls actions
        const formatType = optionKey || 'tooltipFormats';
        dispatch(updateFormatOption(propKey, formatType, option, value));
      }
      dispatch(callQueryAPI(propKey, true));
    } catch (error) {
      console.error('Error updating format:', error);
      // You might want to show a user-friendly error message here
    }
  };

  const handleClose = async (closeFrom: any, queryParam?: any) => {
    // close from could be agg, time grain, windowfn, override etc. queryParam is the option that we selected for change
    setAnchorEl(null);
    setShowOptions(false);
    let updateDispName = true;

    if (closeFrom === 'agg' || closeFrom === 'timeGrain') {
      var field2 = JSON.parse(JSON.stringify(field));

      if (closeFrom === 'agg') {
        if (field2.agg === queryParam) {
          updateDispName = false;
        }
        field2.agg = queryParam;
      } else if (closeFrom === 'timeGrain') {
        if (field2.timeGrain === queryParam) {
          updateDispName = false;
        }
        field2.timeGrain = queryParam;
        if (
          chartType !== 'table' &&
          chartType !== 'crossTab' &&
          (axisTitle === 'Measure' || axisTitle === 'X' || axisTitle === 'Y')
        ) {
          field2.agg = 'count';
        }
      }

      const roundingDigits = getRoundingDigits(field2);
      dispatch(
        updateFormatOption(propKey, 'tooltipFormats', 'measureFormats', {
          ...tooltipFormats.measureFormats,
          [field2.uId]: {
            ...tooltipFormats.measureFormats[field2.uId],
            roundingDigits: roundingDigits,
          },
        }),
      );

      if (chartType === 'richText') {
        //--------- Rich text will be handled later & this action is for measure axis but require a change-------//
        // dispatch(
        //   editChartPropItemForDm('updateQuery', {
        //     propKey,
        //     binIndex: bIndex,
        //     itemIndex,
        //     item: field2,
        //   }),
        // );
      } else {
        if (updateDispName) {
          field2 = setDisplayName(field2, binName, chartProp.properties[propKey].chartType);
          field2.displayname = findNewDisplayName(
            chartProp.properties[propKey].chartAxes,
            field2,
            tooltipFormats,
          );

          field2.isTextRenamed = false;

          if (!field2.isAggregated) {
            field2.displayname = findNewDisplayName(
              chartProp.properties[propKey].chartAxes,
              field2,
            );
            field2.isTextRenamed = false;
          }

          const currentExtraMeasureField = tooltipFormats.extraMeasureField[bIndex];
          const updatedFields = [...currentExtraMeasureField.fields];
          updatedFields[itemIndex] = {
            ...updatedFields[itemIndex],
            displayname: field2.displayname,
            timeGrain: field2.timeGrain,
            agg: field2.agg,
          };

          const updatedExtraMeasureField = [...extraMeasureField];
          updatedExtraMeasureField[bIndex] = {
            ...updatedExtraMeasureField[bIndex],
            fields: updatedFields,
          };

          handleUpdateFormat('extraMeasureField', updatedExtraMeasureField, 'tooltipFormats');
        }
      }
    }
  };

  const handleOverrideOnClick = (event: any, optionName: any) => {
    dispatch(SetModifyOptions(propKey, 'filter_override_open', null));
    if (optionName.id === 'override') {
      setAnchorEl(null);
      setOverrideFn(true);
      setShowTooltip(false);

      let oldChartAxes = JSON.parse(JSON.stringify(chartProp.properties[propKey].chartAxes || []));
      let overRideAxes = JSON.parse(
        JSON.stringify(extraMeasureField[bIndex].fields[itemIndex].override || []),
      );

      // dispatch(
      //   createExtraMeasureChartAxesForUID(
      //     propKey,
      //     extraMeasureField[bIndex].fields[itemIndex].uId,
      //     extraMeasureField[bIndex].fields[itemIndex].override ? overRideAxes : oldChartAxes,
      //   ),
      // );

      dispatch(
        createChartAxesForUID(
          propKey,
          extraMeasureField[bIndex].fields[itemIndex].uId,
          extraMeasureField[bIndex].fields[itemIndex].override ? overRideAxes : oldChartAxes,
        ),
      );

      dispatch(
        enableOverrideForExtraMeasure(propKey, extraMeasureField[bIndex].fields[itemIndex].uId),
      );
    } else {
      let field2 = JSON.parse(JSON.stringify(field));
      setAnchorEl(null);
      field2.disableReportFilterForOverride = !field2.disableReportFilterForOverride;
      dispatch(updateExtraMeasureField(propKey, bIndex, itemIndex, field2));
    }
  };

  const handleWindowFunctionOnClick = (event?: React.MouseEvent) => {
    dispatch(SetModifyOptions(propKey, 'axis', null));
    if (event) {
      setAnchorElement(event.currentTarget);
    }
    setShowTooltip(false);
    setWindowFunction(true);
    handleClose('clickOutside');
  };

  const handleRollup = () => {
    let field2 = JSON.parse(JSON.stringify(field));
    setAnchorEl(null);
    setShowTooltip(false);
    field2.rollupDepth = !field2.rollupDepth;
    field2.isManual = field2.rollupDepth;

    //----------this dispatch is related to measure axis. Will be changed later ---------//

    // dispatch(
    //   editChartPropItem('updateQuery', {
    //     propKey,
    //     binIndex: bIndex,
    //     itemIndex,
    //     item: field2,
    //     currentChartAxesName,
    //   }),
    // );

    let _charAxesFields = extraMeasureField[bIndex].fields;

    if (field2.rollupDepth) {
      if (
        ['crossTab', 'heatmap', 'boxPlot', 'bubbleMap'].includes(
          chartProp.properties[propKey].chartType,
        )
      ) {
        let chartAxesOneFields = chartProp.properties[propKey][currentChartAxesName][0].fields;
        let chartAxesTwoFields = chartProp.properties[propKey][currentChartAxesName][0].fields;

        if (chartAxesOneFields?.length > 0) {
          for (let i = 0; i < chartAxesOneFields?.length; i++) {
            updateFieldRollupProperty(1, i, false);
          }
        }

        if (chartAxesTwoFields?.length > 0) {
          for (let i = 0; i < chartAxesTwoFields?.length; i++) {
            if (itemIndex !== i) {
              updateFieldRollupProperty(2, i, false);
            }
          }
        }
      } else {
        for (let i = 0; i < _charAxesFields?.length; i++) {
          if (itemIndex !== i) {
            let _field = _charAxesFields[i];

            if (_field) {
              let _tempField = JSON.parse(JSON.stringify(_field));

              if (_tempField) {
                _tempField.rollupDepth = false;

                //----------this dispatch is related to measure axis. Will be changed later ---------//

                // dispatch(
                //   editChartPropItem('updateQuery', {
                //     propKey,
                //     binIndex: bIndex,
                //     itemIndex: i,
                //     item: _tempField,
                //     currentChartAxesName,
                //   }),
                // );
              }
            }
          }
        }
      }
    } else {
      // ---------------will be changed when we add window function abd override functionality ---------//
      // updateFieldRollupProperty(
      //   1,
      //   chartProp.properties[propKey][currentChartAxesName][1].fields?.length - 1,
      //   true,
      // );

      let _field = _charAxesFields[_charAxesFields?.length - 1];

      if (_field) {
        let _tempField = JSON.parse(JSON.stringify(_field));

        if (_tempField) {
          _tempField.rollupDepth = true;

          //----------this dispatch is related to measure axis. Will be changed later ---------//

          // dispatch(
          //   editChartPropItem('updateQuery', {
          //     propKey,
          //     binIndex: bIndex,
          //     itemIndex: _charAxesFields?.length - 1,
          //     item: _tempField,
          //     currentChartAxesName,
          //   }),
          // );
        }
      }
    }

    function updateFieldRollupProperty(binIndex: number, index: number, enable: boolean) {
      let _field = allMeasureFields[index];

      if (_field) {
        let _tempField = JSON.parse(JSON.stringify(_field));

        if (_tempField) {
          _tempField.rollupDepth = enable;

          //----------this dispatch is related to measure axis. Will be changed later ---------//

          // dispatch(
          //   editChartPropItem('updateQuery', {
          //     propKey,
          //     binIndex: binIndex,
          //     itemIndex: index,
          //     item: _tempField,
          //     currentChartAxesName,
          //   }),
          // );
        }
      }
    }
  };

  let menuStyle = { fontSize: '12px', padding: '2px 1.5rem' };
  let menuSelectedStyle = {
    fontSize: '12px',
    padding: '2px 1.5rem',
    backgroundColor: 'rgba(175, 153, 219,0.3)',
    '&:hover': {
      backgroundColor: 'rgba(175, 153, 219, 0.5)',
    },
  };

  const getAllMeasureFields = (): any[] => {
    let allMeasureFields: any[] = [];
    if (extraMeasureField && extraMeasureField[bIndex]) {
      allMeasureFields = [...extraMeasureField[bIndex].fields];
    }
    return allMeasureFields;
  };

  const RenderMenu = () => {
    let aggr = [];
    let timegrain = [];
    let windowfn = [];
    let overRideMenuList = [];
    //Pass Measure so that options will be same as for Measure Field
    let options = CardOption(binName, field);
    if (options) {
      aggr = options[0];
      timegrain = options[1];
      windowfn = options[2];
      overRideMenuList = options[3];
    }

    // const hasOtherOptions =
    //   aggr?.length > 0 ||
    //   timegrain?.length > 0 ||
    //   (windowfn?.length > 0 &&
    //     !['gauge', 'funnel', 'simplecard'].includes(chartType)) ||
    //   (overRideMenuList?.length > 0 && chartType !== 'simplecard');

    const isItemDisabled = (optionValue: string) => {
      if (field?.isAggregated) {
        return true;
      }
      if (chartType === 'table' || chartType === 'crossTab') {
        return false;
      }
      if (chartType === 'bubbleMap') {
        if (optionValue === 'Min' || optionValue === 'Max' || optionValue === 'Count Unique') {
          return true;
        }
      } else {
        if (field.timeGrain === 'date' && (optionValue === 'Min' || optionValue === 'Max')) {
          return true;
        }
      }
      return false;
    };
    const isDefaultAxis = ['X', 'Y', 'Measure'].includes(axisTitle);
    const shouldShowDivider =
      isDefaultAxis || (!isDefaultAxis && (aggr.length > 0 || timegrain.length > 0));

    return (
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose('clickOutside')}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
          },
        }}
      >
        {aggr?.length > 0
          ? aggr?.map((opt: any, idx: number) => {
              const agg = extraMeasureField[bIndex].fields[itemIndex]?.agg;
              const isChecked = agg?.toUpperCase() === opt?.name?.toUpperCase();
              return (
                <AggregationMenuItem
                  key={opt?.id}
                  isChecked={isChecked}
                  onClick={() => handleClose('agg', opt.id)}
                  optionName={opt?.name}
                  isDisabled={opt?.name ? isItemDisabled(opt?.name) : true}
                  menuStyle={menuStyle}
                  menuSelectedStyle={menuSelectedStyle}
                />
              );
            })
          : null}

        {chartType === 'gauge' || chartType === 'funnel' || chartType === 'simplecard' ? null : (
          <Divider />
        )}

        {(chartType === 'gauge' && timegrain?.length > 0) ||
        (chartType === 'funnel' && timegrain?.length > 0) ||
        (chartType === 'simplecard' && timegrain?.length > 0) ? (
          <Divider />
        ) : null}

        {timegrain?.length > 0
          ? timegrain?.map((opt2: any, idx: number) => {
              const isChecked =
                extraMeasureField[bIndex].fields[itemIndex]?.agg?.toUpperCase() ===
                opt2.name?.toUpperCase();
              return (
                <TimeGrainMenuItem
                  key={idx}
                  isChecked={isChecked}
                  onClick={() => handleClose('timeGrain', opt2.id)}
                  optionName={opt2.name}
                  isSelected={opt2.id === field.timeGrain}
                  menuStyle={menuStyle}
                  menuSelectedStyle={menuSelectedStyle}
                />
              );
            })
          : null}

        {chartType === 'gauge' ||
        chartType === 'funnel' ||
        chartType === 'simplecard' ? null : timegrain?.length > 0 ? (
          <Divider />
        ) : null}

        {chartType === 'gauge' || chartType === 'funnel' || chartType === 'simplecard'
          ? null
          : windowfn?.length > 0
            ? [...windowfn.filter((item: any) => item?.id === 'windowfn')]?.map(
                (opt: any, idx: number) => {
                  const isChecked = extraMeasureField[bIndex].fields[itemIndex]?.windowfn;
                  return (
                    <WindowFunctionMenuItem
                      key={opt?.id}
                      isChecked={isChecked}
                      onClick={handleWindowFunctionOnClick}
                      optionName={opt?.name}
                      isDisabled={windowFunctionDisable}
                    />
                  );
                },
              )
            : null}

        {chartType === 'simplecard'
          ? null
          : overRideMenuList?.length > 0
            ? overRideMenuList
                .filter((opt: any) => opt && opt.id)
                .map((opt: any, idx: number) => {
                  const isChecked =
                    (opt.id === 'override' && field?.override) ||
                    (opt.id === 'disableFilter' && field?.disableReportFilterForOverride);
                  const isDisabled = disable.override || !!field?.hasOverridedFlow;

                  return (
                    <>
                      <Divider />
                      <IgnoreReportFilterMenuItem
                        isChecked={field?.disableReportFilterForOverride ?? false}
                        optionName={'Ignore Report filter'}
                        isDisabled={!field.override}
                        menuStyle={menuStyle}
                        onClick={(e) => {
                          let field2 = JSON.parse(JSON.stringify(field));
                          field2.disableReportFilterForOverride =
                            !field.disableReportFilterForOverride;

                          dispatch(updateExtraMeasureField(propKey, bIndex, itemIndex, field2));
                          dispatch(callQueryAPI(propKey, true));

                          setAnchorEl(null);
                          setShowOptions(false);
                        }}
                      />
                      <OverrideMenuItem
                        key={idx}
                        isChecked={isChecked}
                        onClick={(e) => handleOverrideOnClick(e, opt)}
                        optionName={opt.name}
                        isDisabled={isDisabled}
                        menuStyle={{ fontSize: '12px', padding: '2px 1.5rem' }}
                      />
                    </>
                  );
                })
            : null}
        {shouldShowDivider && <Divider />}

        <MenuOptions
          uID={uID}
          windowfn={windowfn}
          axisTitle={axisTitle}
          handleClose={handleClose}
          setRenameFunction={setRenameFunction}
          setShowTooltip={setShowTooltip}
          handleRollup={handleRollup}
          menuStyle={menuStyle}
          field={field}
        />
        <MenuItem
          onClick={() => {
            handleClose('clickOutside');
            setShowSourceDetails(true);
          }}
          sx={menuStyle}
        >
          Source Details
        </MenuItem>
      </Menu>
    );
  };

  useEffect(() => {
    (async () => {
      if (field?.SavedCalculationUUID) {
        const isSavedCalculation = savedCalcs.find(
          (calculation: any) =>
            calculation.uuid === field.SavedCalculationUUID &&
            calculation.datasetId === selectedDs.id,
        );
        if (isSavedCalculation) {
          let hasMissing = false;
          const fields = isSavedCalculation.calculationInfo.fields;
          for (const fieldKey of Object.keys(fields)) {
            try {
              const res = await checkIfMissingColumn(
                fields[fieldKey],
                selectedDs,
                tabTileProps.tablesForSelectedDataSets[selectedDs.id],
              );
              if (res.isMissing) {
                hasMissing = true;
                break;
              }
            } catch (err) {
              hasMissing = true;
              break;
            }
          }
          setIsShowWarning(hasMissing);
        } else {
          setIsShowWarning(true);
        }
        return;
      }
      const res = await checkIfMissingColumn(
        field,
        selectedDs,
        tabTileProps.tablesForSelectedDataSets[selectedDs.id],
        (field as any).originalDataSetId !== selectedDs.id,
      );
      console.log(res);
      const showWarning =
        (field as any).originalDataSetId !== selectedDs.id
          ? Boolean(!res.newTableId)
          : Boolean(res.isMissing);
      setIsShowWarning(showWarning);
    })();
  }, [
    chartProp.properties[propKey].selectedDs.id,
    tooltipFormats?.extraMeasureField?.[0]?.fields.length,
    chartProp.properties[propKey]?.oldSelectedDs?.id,
  ]);

  const duplicateIndex =
    allMeasureFields?.slice(0, itemIndex).filter((f: any) => f.fieldname === field.fieldname)
      ?.length || 0;

  return field ? (
    <div
      className={isShowWarning ? 'axisFieldWarning' : 'axisField'}
      // style={windowFunction || overrideFn ? { border: '1.5px solid #2bb9bb' } : {}}
      style={{
        border: windowFunction || overrideFn ? '1.5px solid #2bb9bb' : '',
        marginBottom: '0.5rem',
      }}
      onMouseOver={(event: any) => {
        setShowOptions(true);
        if (event.target.classList.contains('columnName')) setShowTooltip(true);
      }}
      onMouseLeave={() => {
        if (!open) {
          setShowOptions(false);
        }
        setShowTooltip(false);
      }}
    >
      <CustomTooltip
        isShowWarning={isShowWarning}
        title={
          isShowWarning ? (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Warning style={{ color: '#850000ff', fontSize: '16px', marginRight: '4px' }} />
              {field.displayname}
            </div>
          ) : (
            field.displayname
          )
        }
        arrow
        open={showTooltip}
        placement="left-start"
        style={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: '8px',
          fontSize: '13px',
        }}
      >
        <span style={{ display: 'inline-block', fontSize: 0, lineHeight: 0 }}></span>
      </CustomTooltip>
      <button
        type="button"
        className="buttonCommon columnClose"
        onClick={deleteItem}
        title="Remove field"
        style={showOptions ? { visibility: 'visible' } : { visibility: 'hidden' }}
      >
        <CloseRoundedIcon style={{ fontSize: '13px', margin: 'auto' }} />
      </button>

      <span
        className="columnName"
        style={{
          fontWeight: 'normal',
        }}
      >
        {field.displayName2 ?? field.fieldname}
      </span>
      {isShowWarning && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyItems: 'center',
            marginRight: '0.5rem',
          }}
        >
          <Tooltip
            arrow
            placement="top"
            title={'This column is missing in the dataset'}
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <Warning
              style={{
                color: '#850000ff',
                flexShrink: 0,
              }}
              fontSize="small"
            />
          </Tooltip>
        </div>
      )}
      {measureMappingWarning && (
        <Tooltip
          title="This measure is not mapped. Map it to have an effect of Measure Level Control in tooltip. Go to Chart controls -> User Level Filter -> Measure Level Control -> Then choose from Measure Mapping drop down."
          arrow
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingRight: '5px',
            }}
          >
            <WarningIcon
              style={{
                cursor: 'pointer',
                color: 'orange',
                fontSize: '1.5em',
                transition: 'color 0.3s, font-size 0.3s',
              }}
            ></WarningIcon>
          </span>
        </Tooltip>
      )}

      {/* window function have any values in state, then window icon will get enable */}
      {chartType === 'gauge' ||
      chartType === 'funnel' ||
      chartType === 'simplecard' ? null : field.windowfn ? (
        <WindowFunctionButton onClick={handleWindowFunctionOnClick} isEnabled={true} />
      ) : null}
      {chartType === 'simplecard' ? null : field.override ? (
        <OverrideFunction onClick={(e) => handleOverrideOnClick(e, { id: 'override' })} />
      ) : null}
      {chartType === 'simplecard' ? null : field.rollupDepth ? (
        <RollUpButton onClick={handleRollup} />
      ) : null}

      <AggregationDisplay
        isAggregated={field.isAggregated || false}
        agg={field.agg}
        timeGrain={field.timeGrain}
        prefix={field.prefix}
      />

      <button
        type="button"
        className="buttonCommon columnDown"
        data-testid={`Tooltip_Measure_${field.fieldname}_${duplicateIndex}`}
        onClick={(e: any) => {
          handleClick(e);
        }}
        title="Click for Menu Options"
        style={
          showOptions
            ? { visibility: 'visible', paddingLeft: '1px' }
            : { visibility: 'hidden', paddingLeft: '1px' }
        }
      >
        <KeyboardArrowDownRoundedIcon style={{ fontSize: '14px', margin: 'auto' }} />
      </button>

      <RenderMenu />
      {windowFunction ? (
        <WindowFunction
          anchorElm={anchorElment}
          haswindowfn={windowFunction}
          setWindowfn={setWindowFunction}
          propKey={propKey}
          bIndex={bIndex}
          itemIndex={itemIndex}
          tooltipFormats={tooltipFormats}
        />
      ) : null}

      {renameFunction && (
        <RenameFunction
          renamefn={renameFunction}
          setRenamefn={setRenameFunction}
          propKey={propKey}
          bIndex={bIndex}
          itemIndex={itemIndex}
          tooltipFormats={tooltipFormats}
        />
      )}

      {showSourceDetails && (
        <SourceDetails field={field} open={showSourceDetails} setOpen={setShowSourceDetails} />
      )}

      <SnackBar
        show={showPopup}
        message={messages.playbook.WindowFunctionRemoved}
        onClose={() => setShowPopup(false)}
      />
    </div>
  ) : null;
};

export default ExtraMeasureCard;
